import { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider } from '@/components/theme-provider';

// Components
import Sidebar from './components/Sidebar';
import Dashboard from './pages/Dashboard';
import Licenses from './pages/Licenses';
import Users from './pages/Users';
import Products from './pages/Products';
import Customers from './pages/Customers';
import Partners from './pages/Partners';
import PurchaseOrders from './pages/PurchaseOrders';
import SystemSignatures from './pages/SystemSignatures';
import SystemLogs from './pages/SystemLogs';
import Domains from './pages/Domains';
import Roles from './pages/Roles';
import Organizations from './pages/Organizations';
import ApiKeys from './pages/ApiKeys';
import Activations from './pages/Activations';
import VerificationPackages from './pages/VerificationPackages';
import Analytics from './pages/Analytics';
import ActivityLogs from './pages/ActivityLogs';
import Notifications from './pages/Notifications';
import Webhooks from './pages/Webhooks';
import Billing from './pages/Billing';
import Settings from './pages/Settings';
import MFA from './pages/MFA';
import Login from './pages/Login';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    // Check if user is authenticated
    const token = localStorage.getItem('token');
    if (token) {
      setIsAuthenticated(true);
      // Fetch user data
      fetchUser(token);
    }
  }, []);

  const fetchUser = async (token: string) => {
    try {
      const apiUrl = import.meta.env.VITE_API_URL || '';
      const response = await fetch(`${apiUrl}/api/users/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUser(data.data);
      } else {
        // Token invalid, clear it
        localStorage.removeItem('token');
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Failed to fetch user:', error);
    }
  };

  const handleLogin = (token: string, userData: any) => {
    localStorage.setItem('token', token);
    setIsAuthenticated(true);
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    setIsAuthenticated(false);
    setUser(null);
  };

  if (!isAuthenticated) {
    return (
      <ThemeProvider defaultTheme="dark">
        <Login onLogin={handleLogin} />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider defaultTheme="dark">
      <Router>
        <div className="flex h-screen bg-background">
          <Sidebar onLogout={handleLogout} user={user} />

          <main className="flex-1 overflow-y-auto">
            <Routes>
              <Route path="/" element={<Dashboard user={user} />} />
              <Route path="/licenses" element={<Licenses user={user} />} />
              <Route path="/users" element={<Users />} />
              <Route path="/products" element={<Products />} />
              <Route path="/customers" element={<Customers />} />
              <Route path="/partners" element={<Partners />} />
              <Route path="/purchase-orders" element={<PurchaseOrders />} />
              <Route path="/system-signatures" element={<SystemSignatures />} />
              <Route path="/system-logs" element={<SystemLogs />} />
              <Route path="/domains" element={<Domains />} />
              <Route path="/roles" element={<Roles />} />
              <Route path="/organizations" element={<Organizations />} />
              <Route path="/api-keys" element={<ApiKeys />} />
              <Route path="/activations" element={<Activations />} />
              <Route path="/verification" element={<VerificationPackages />} />
              <Route path="/analytics" element={<Analytics />} />
              <Route path="/activity-logs" element={<ActivityLogs />} />
              <Route path="/notifications" element={<Notifications />} />
              <Route path="/webhooks" element={<Webhooks />} />
              <Route path="/billing" element={<Billing />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/mfa" element={<MFA />} />
            </Routes>
          </main>
        </div>
      </Router>
    </ThemeProvider>
  );
}

export default App;
